/**
 * The person's own notifications (an approval asked of them, a template made) in the top bar: how
 * many are unread and the latest ones, each opening where it points and read once opened.
 */
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Inbox } from 'lucide-react'
import { api } from '../../lib/api'
import { formatRelative } from '../../lib/utils'
import { useT } from '../../i18n'

interface Note { id: string; title: string; message: string; link: string | null; isRead: boolean; createdAt: string }

export function InboxMenu() {
  const t = useT()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)
  const { data } = useQuery({
    queryKey: ['inbox'],
    queryFn: () => api.get('/inbox').then(r => r.data.data as { unread: number; items: Note[] }),
    refetchInterval: 60_000,
  })
  useEffect(() => {
    if (!open) return
    const away = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false) }
    const escape = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', away)
    document.addEventListener('keydown', escape)
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', escape) }
  }, [open])
  const unread = data?.unread ?? 0
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['inbox'] })
  const openNote = async (n: Note) => {
    setOpen(false)
    if (!n.isRead) await api.post(`/inbox/${n.id}/read`).catch(() => undefined)
    void refresh()
    if (n.link) navigate(n.link)
  }
  const readAll = async () => {
    await api.post('/inbox/read-all').catch(() => undefined)
    void refresh()
  }
  return (
    <div className="inbox" ref={box}>
      <button className="topbar-alert-btn" aria-label={unread ? t('nav.inboxUnread', { count: unread }) : t('nav.inbox')} aria-expanded={open} aria-haspopup="true" onClick={() => setOpen(!open)}>
        <Inbox size={18} />
        {unread > 0 && <span className="topbar-badge">{unread > 9 ? '9+' : unread}</span>}
      </button>
      {open && (
        <div className="inbox-panel" role="dialog" aria-label={t('nav.inbox')}>
          <div className="inbox-head">
            <strong>{t('nav.inbox')}</strong>
            {unread > 0 && <button className="btn btn-ghost btn-sm" onClick={readAll}>{t('nav.inboxReadAll')}</button>}
          </div>
          {!data?.items.length ? <div className="inbox-empty">{t('nav.inboxEmpty')}</div> : (
            <ul>
              {data.items.map(n => (
                <li key={n.id}>
                  <button className={`inbox-item${n.isRead ? '' : ' unread'}`} onClick={() => openNote(n)}>
                    <span className="inbox-title">{n.title}</span>
                    <span className="inbox-message">{n.message}</span>
                    <span className="inbox-when">{formatRelative(n.createdAt)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
